import { promisify, copyPropertiesWhen, toKebabCase, toFixedRange, Debounce, checkTimeout, Identifier, ChildIdentifier, getColumn, pathWalk, random, toFit, toWrapString, base64ToHex, toByteFormat, stripTag, isNumber, isNone, isNumeric } from '@js-dot/core';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, ChangeDetectorRef, Directive, Injector, ElementRef, Injectable, Inject, ViewContainerRef, Component, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, createComponent, InjectionToken, Optional, Input, Pipe, HostListener, EventEmitter, Output, NgModule } from '@angular/core';
import { BehaviorSubject, Subject, Observable, mergeMap, map, fromEvent, Subscription, timer } from 'rxjs';
import { RxClassSwitch, RxClassToggle } from '@js-dot/rx/view';
import { map as map$1, distinctUntilChanged, debounceTime, filter, takeUntil } from 'rxjs/operators';
import { getVar, setVar, Styles } from '@js-dot/ui/style';
import { __decorate } from 'tslib';
import { checkSubscription, observify } from '@js-dot/rx';
import { renderDate, renderHost, renderTel, renderNonKr, renderParagraph, renderUrlToLink } from '@js-dot/ui';
import { getPlural } from '@js-dot/text';
import * as i1 from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
const _c0 = ["marker"];
function DotPoolComponent_ng_template_0_Template(rf, ctx) {}
function getNode(element) {
  return element['nativeElement'] || element;
}

/**
 * @deprecated
 */
function selectsAll(containers) {
  containers.forEach(selectsAllEachFunction);
}
/**
 * @deprecated
 */
function selectsAllEachFunction(container) {
  container.selectAll();
}
function unselectsAll(containers) {
  containers.forEach(unselectsAllEachFunction);
}
/**
 * @deprecated
 */
function unselectsAllEachFunction(container) {
  container.unselectAll();
}
function invertsAll(containers) {
  containers.forEach(invertsAllEachFunction);
}
/**
 * @deprecated
 */
function invertsAllEachFunction(container) {
  container.invertAll();
}
/**
 * @deprecated
 */
function selectAll(containers) {
  containers.forEach(selectAllEachFunction);
}
/**
 * @deprecated
 */
function selectAllEachFunction(container) {
  container.select();
}
/**
 * @deprecated
 */
function unselectAll(containers) {
  containers.forEach(unselectAllEachFunction);
}
/**
 * @deprecated
 */
function unselectAllEachFunction(container) {
  container.unselect();
}
/**
 * @deprecated
 */
function invertAll(containers) {
  containers.forEach(invertAllEachFunction);
}
/**
 * @deprecated
 */
function invertAllEachFunction(container) {
  container.invert();
}

/**
 * Get function for *ngFor(trackBy:)
 *
 * @example
 * ```ts
 * interface Foo {
 *     id: string
 * }
 *
 * @Component({
 *     template: `
 *     		<ng-container *ngFor="let foo of items; trackBy: usingId" />
 *     `
 * })
 * class FooComponent {
 *     usingId = getTracker<Foo>('id');
 * }
 * ```
 * @param propertyName
 */
function getTracker(propertyName) {
  return getTracker[propertyName] || (getTracker[propertyName] = function (index, item) {
    return item[propertyName];
  });
}

/**
 * Write signal as promisify value
 *
 * @param promise
 * @param signal
 */
function lazySet(promise, signal) {
  promisify(promise).then(result => {
    signal.set(result);
  });
}

/**
 * Base-class for extends Async Pipe
 *
 * @example
 * ```ts
 * @Pipe({
 * 	name: 'foo',
 * 	// Should be set `false`
 * 	pure: false
 * })
 * class FooPipe extends WithAsyncPipeBase implements PipeTransform {
 *
 * 	// Set transform function for typing
 * 	transform(value: string){
 * 		return this.transformWithAsync(arguments)
 * 	}
 *
 *	// Set real value before async
 * 	getValue(value: string){
 * 		return Promise.resolve(value);
 * 	}
 * }
 * ```
 */
class WithAsyncBase {
  #withAsyncPipe;
  #withAsyncLastArgs;
  #withAsyncLastValue;
  constructor() {
    this.#withAsyncPipe = new AsyncPipe(inject(ChangeDetectorRef));
  }
  ngOnDestroy() {
    this.#withAsyncPipe.ngOnDestroy();
  }
  /**
   * Pipe Transforming with Async Pipe.
   *
   * Just pass the `arguments` in transform function
   * @param args
   */
  transformWithAsync(args) {
    const lastArgs = this.#withAsyncLastArgs;
    if (lastArgs) {
      let current = lastArgs.length;
      while (current-- > 0) {
        if (args[current] === lastArgs[current]) {
          continue;
        }
        this.#withAsyncLastValue = this.getValue(...args);
        break;
      }
    } else {
      this.#withAsyncLastValue = this.getValue(...args);
    }
    this.#withAsyncLastArgs = args;
    return this.#withAsyncPipe.transform(this.#withAsyncLastValue);
  }
  static ɵfac = function WithAsyncBase_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || WithAsyncBase)();
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: WithAsyncBase
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WithAsyncBase, [{
    type: Directive
  }], () => [], null);
})();
class InheritBehaviorSubject extends BehaviorSubject {
  pipe(...pipes) {
    // @ts-ignore
    return super.pipe(...pipes);
  }
}
copyPropertiesWhen(InheritBehaviorSubject, 'pipe');
function subscribeCycleOverride(target) {
  const {
    subscribe: SUBSCRIBE
  } = target.prototype;
  target.prototype.subscribe = function (...args) {
    const subscription = SUBSCRIBE.apply(this, args);
    const {
      unsubscribe: UNSUBSCRIBE
    } = subscription;
    if (!this.firstSubscribed) {
      this.firstSubscribed = true;
      this.firstSubscribe$.next();
    }
    subscription.unsubscribe = () => {
      UNSUBSCRIBE.apply(subscription);
      if (!this.observed) {
        this.firstSubscribed = false;
        this.lastUnsubscribe$.next();
      }
    };
    return subscription;
  };
}
class SubscribeCycleSubject extends Subject {
  firstSubscribed = false;
  firstSubscribe$ = new Subject();
  lastUnsubscribe$ = new Subject();
}
subscribeCycleOverride(SubscribeCycleSubject);
class InheritSubject extends SubscribeCycleSubject {
  pipe(...pipes) {
    // @ts-ignore
    return super.pipe(...pipes);
  }
}
copyPropertiesWhen(InheritSubject, 'pipe');

/**
 * Mixin Component Base
 */
// tslint:disable-next-line:directive-class-suffix
class MixinComponent {
  _injector;
  /**
   * Initialized condition until mixin component
   */
  _mixedInit;
  /**
   * Component Container Element
   */
  _mixedNode;
  /**
   * Components Mixin
   * @param mixins
   */
  static apply(...mixins) {
    return mixins.reduce((baseClass, mixin) => {
      return mixin(baseClass);
    }, MixinComponent);
  }
  constructor() {
    this._injector = inject(Injector);
    this._mixedNode = this._injector.get(ElementRef).nativeElement;
  }
  ngOnInit() {
    this._mixedInit = true;
  }
  ngAfterContentInit() {}
  ngOnChanges(changes) {}
  ngOnDestroy() {}
  static ɵfac = function MixinComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || MixinComponent)();
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: MixinComponent,
    features: [i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MixinComponent, [{
    type: Directive
  }], () => [], null);
})();

/**
 * ClassSwitch With Angular
 * @inheritDoc
 */
class NgClassSwitch extends RxClassSwitch {
  constructor(node, option) {
    super(node instanceof ElementRef ? node.nativeElement : node, option);
  }
}

/**
 * ClassToggle With Angular
 * @inheritDoc
 */
class NgClassToggle extends RxClassToggle {
  constructor(node, trueClass, falseClass) {
    super(node instanceof ElementRef ? node.nativeElement : node, trueClass, falseClass);
  }
}
class SubscribeCycleBehaviorSubject extends BehaviorSubject {
  firstSubscribed = false;
  firstSubscribe$ = new Subject();
  lastUnsubscribe$ = new Subject();
}
subscribeCycleOverride(SubscribeCycleBehaviorSubject);
class DotAssetService {
  _head;
  _scripts = {};
  constructor(document) {
    this._head = document.head;
  }
  /**
   * Dynamic import external script as sequential load
   *
   * @example
   * 	```typescript
   * 	@Component()
   * 	export class FooComponent {
   * 	    constructor(_asset: DotAssetService){
   * 	        _asset.script$('/assets/foo.js', '/assets/bar.js')
   * 	        	.subscribe(([fooScript, barScript]) => {
   * 	        	 	// do something
   * 	        	})
   * 	    }
   * 	}
   * 	```
   * @param options
   */
  script$(...src_or_options) {
    return this.#scriptLoad(src_or_options);
  }
  #scriptLoad(src_or_options, scripts = []) {
    const {
      _head,
      _scripts
    } = this;
    let option = src_or_options.shift();
    if (typeof option === 'string') {
      option = {
        url: option,
        reload: false,
        module: false
      };
    }
    const {
      url,
      reload,
      module
    } = option;
    let subject = _scripts[url];
    if (reload || !subject || !subject.value) {
      subject = new Observable(observer => {
        const script = document.createElement('script');
        if (module) {
          script.type = 'module';
        }
        script.onload = () => {
          _scripts[url] = new BehaviorSubject(script);
          observer.next(script);
          observer.complete();
        };
        script.onerror = reason => {
          observer.error(reason);
        };
        script.src = url;
        this._head.appendChild(script);
      });
    }
    return src_or_options.length ? subject.pipe(mergeMap(script => {
      scripts[scripts.length] = script;
      return this.#scriptLoad(src_or_options, scripts);
    })) : subject.pipe(map(script => {
      return scripts.concat([script]);
    }));
  }
  static ɵfac = function DotAssetService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotAssetService)(i0.ɵɵinject(DOCUMENT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotAssetService,
    factory: DotAssetService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotAssetService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: Document,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }], null);
})();
class DotRendererService {
  document;
  _renderer;
  constructor(document, _rendererFactory) {
    this.document = document;
    this._renderer = _rendererFactory.createRenderer(null, null);
  }
  selectRootElement(selectorOrNode, preserveContent) {
    return this._renderer.selectRootElement(selectorOrNode, preserveContent);
  }
  createElement(name, namespace) {
    return this._renderer.createElement(name, namespace);
  }
  createComment(value) {
    return this._renderer.createComment(value);
  }
  createText(value) {
    return this._renderer.createText(value);
  }
  insertBefore(parent, newChild, refChild, isMove) {
    return this._renderer.insertBefore(parent, newChild, refChild, isMove);
  }
  appendChild(parent, newChild) {
    return this._renderer.appendChild(parent, newChild);
  }
  removeChild(parent, oldChild, isHostElement) {
    return this._renderer.removeChild(parent, oldChild, isHostElement);
  }
  destroy() {
    return this._renderer.destroy();
  }
  destroyNode(node) {
    return this._renderer.destroyNode(node);
  }
  setProperty(element, name, value) {
    this._renderer.setProperty(element, name, value);
  }
  setValue(node, value) {
    return this._renderer.setValue(node, value);
  }
  setAttribute(el, name, value, namespace) {
    return this._renderer.setAttribute(el, name, value, namespace);
  }
  removeAttribute(el, name, namespace) {
    return this._renderer.removeAttribute(el, name, namespace);
  }
  setStyle(element, name, value) {
    const {
      _renderer
    } = this;
    return name.startsWith('--') ? this._renderer.setProperty(element, 'style', name + ': ' + value) : _renderer.setStyle(element, name, value);
  }
  removeStyle(el, style, flags) {
    return this._renderer.removeStyle(el, style, flags);
  }
  addClass(element, ...classNames) {
    const {
      _renderer
    } = this;
    if (Array.isArray(classNames[0])) {
      classNames = classNames[0];
    }
    let current = classNames.length;
    while (current-- > 0) {
      _renderer.addClass(element, classNames[current]);
    }
  }
  removeClass(element, ...classNames) {
    const {
      _renderer
    } = this;
    if (Array.isArray(classNames[0])) {
      classNames = classNames[0];
    }
    let current = classNames.length;
    while (current-- > 0) {
      _renderer.removeClass(element, classNames[current]);
    }
  }
  getFragment() {
    return this.document.createDocumentFragment();
  }
  parentNode(node) {
    return this._renderer.parentNode(node);
  }
  nextSibling(node) {
    return this._renderer.nextSibling(node);
  }
  listen(target, eventName, callback) {
    return this._renderer.listen(target, eventName, callback);
  }
  //////////////////////////////
  // Extends
  before(parent, newChild, refChild) {
    const {
      _renderer
    } = this;
    return refChild ? _renderer.insertBefore(parent, newChild, refChild) : _renderer.insertBefore(_renderer.parentNode(parent), newChild, parent);
  }
  append(parent, ...children) {
    const {
      _renderer
    } = this;
    if (children.length === 0) {
      children[0] = parent;
      parent = this.document.body;
    } else {
      if (parent instanceof ElementRef) {
        parent = parent.nativeElement;
      }
    }
    let end = children.length;
    let current = -1;
    let child;
    while (++current < end) {
      child = children[current];
      _renderer.appendChild(parent, child);
      parent = child;
    }
    return child;
  }
  remove(element) {
    const {
      _renderer
    } = this;
    return _renderer.removeChild(_renderer.parentNode(element), element);
  }
  tag(tagName_or_properties, properties) {
    const {
      _renderer
    } = this;
    let tagName = tagName_or_properties;
    switch (typeof tagName) {
      case 'string':
        if (tagName.startsWith('.')) {
          properties = {
            className: tagName.slice(1)
          };
          tagName = 'div';
        }
        break;
      default:
        properties = tagName;
        tagName = 'div';
    }
    const element = _renderer.createElement(tagName);
    if (!properties) {
      return element;
    }
    if (typeof properties === 'string') {
      properties = {
        className: properties
      };
    }
    for (let property in properties) {
      if (!properties.hasOwnProperty(property)) {
        continue;
      }
      const value = properties[property];
      if (typeof value === 'object') {
        switch (property) {
          case 'style':
            for (let subKey in value) {
              if (value.hasOwnProperty(subKey)) {
                continue;
              }
              _renderer.setStyle(element, subKey, value[subKey]);
            }
            break;
          default:
            throw new RangeError('Not Implemented');
        }
        continue;
      }
      _renderer.setProperty(element, property, value);
    }
    return element;
  }
  classSwitch(ref) {}
  classToggle(ref) {}
  static ɵfac = function DotRendererService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRendererService)(i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(i0.RendererFactory2));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotRendererService,
    factory: DotRendererService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRendererService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: Document,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }, {
    type: i0.RendererFactory2
  }], null);
})();

/**
 * Default Dynamic Component's ViewContainerRef
 */
class DotPoolComponent {
  viewContainerRef;
  static ɵfac = function DotPoolComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotPoolComponent)();
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DotPoolComponent,
    selectors: [["ng-component"]],
    viewQuery: function DotPoolComponent_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(_c0, 7, ViewContainerRef);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.viewContainerRef = _t.first);
      }
    },
    decls: 2,
    vars: 0,
    consts: [["marker", ""]],
    template: function DotPoolComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵtemplate(0, DotPoolComponent_ng_template_0_Template, 0, 0, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
      }
    },
    encapsulation: 2,
    changeDetection: 0
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotPoolComponent, [{
    type: Component,
    args: [{
      standalone: true,
      template: `
		<ng-template #marker></ng-template>
	`,
      encapsulation: ViewEncapsulation.None,
      changeDetection: ChangeDetectionStrategy.OnPush
    }]
  }], null, {
    viewContainerRef: [{
      type: ViewChild,
      args: ['marker', {
        static: true,
        read: ViewContainerRef
      }]
    }]
  });
})();
/**
 * Dynamic Component Operation
 */
class DotComponentService {
  _render;
  injector;
  applicationRef;
  _document = inject(DOCUMENT);
  _root;
  constructor(_render, injector, applicationRef) {
    this._render = _render;
    this.injector = injector;
    this.applicationRef = applicationRef;
  }
  /**
   * appendStyleComponent component
   * @param viewContainerRef
   * @param component
   * @param data
   * @param option
   */
  append(viewContainerRef, component, data, option) {
    if (!component) {
      component = viewContainerRef;
      viewContainerRef = null;
    }
    let componentRef;
    if (viewContainerRef) {
      componentRef = viewContainerRef.createComponent(component);
    } else {
      const {
        applicationRef,
        _render
      } = this;
      const selector = toKebabCase(component.name.replace(/Component$/, ''));
      componentRef = createComponent(component, {
        environmentInjector: applicationRef.injector,
        hostElement: _render.append(_render.tag(selector.includes('-') ? selector : 'ng-component'))
      });
      applicationRef.attachView(componentRef.hostView);
    }
    const {
      instance,
      changeDetectorRef
    } = componentRef;
    instance.componentRef = componentRef;
    // componentRef.setInput('componentRef', componentRef);
    if (data) {
      for (let key in data) {
        if (data.hasOwnProperty(key)) {
          componentRef.setInput(key, data[key]);
        }
      }
      changeDetectorRef.detectChanges();
    } else {
      changeDetectorRef.markForCheck();
    }
    if (option?.activeFullscreenElement) {
      const {
        fullscreenElement
      } = this._document;
      if (fullscreenElement) {
        fullscreenElement.append(componentRef.instance.node);
      }
    }
    return componentRef;
  }
  /**
   * get instance
   * @param viewContainerRef
   * @param component
   * @param data
   */
  instance(viewContainerRef, component, data) {
    const componentRef = this.append(viewContainerRef, component);
    return componentRef.instance;
  }
  pool(viewContainerRef_or_poolComponent, poolComponent_or_dynamicComponent, dynamicComponent_or_options, options) {
    let viewContainerRef = viewContainerRef_or_poolComponent;
    let poolComponent = poolComponent_or_dynamicComponent;
    let dynamicComponent = dynamicComponent_or_options;
    if (viewContainerRef instanceof ViewContainerRef) {
      options = dynamicComponent;
      dynamicComponent = poolComponent;
      poolComponent = null;
    } else {
      options = dynamicComponent;
      dynamicComponent = poolComponent;
      poolComponent = viewContainerRef;
      viewContainerRef = null;
    }
    if (dynamicComponent.constructor === Object) {
      options = dynamicComponent;
      dynamicComponent = null;
    }
    if (!viewContainerRef) {
      viewContainerRef = this.instance(poolComponent).viewContainerRef;
    } else if (typeof viewContainerRef === 'object' && viewContainerRef['viewContainerRef'] && viewContainerRef['componentRef']) {
      viewContainerRef = viewContainerRef.viewContainerRef;
    }
    if (options) {
      const {
        className
      } = options;
      if (className) {
        this._render.addClass(this._render.parentNode(viewContainerRef.element.nativeElement), className.split(' '));
      }
    }
    return dynamicComponent ? new ComponentStaticPool(this, viewContainerRef, dynamicComponent, options) : new ComponentPool(this, viewContainerRef, options);
  }
  static ɵfac = function DotComponentService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotComponentService)(i0.ɵɵinject(DotRendererService), i0.ɵɵinject(i0.Injector), i0.ɵɵinject(i0.ApplicationRef));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotComponentService,
    factory: DotComponentService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotComponentService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: DotRendererService
  }, {
    type: i0.Injector
  }, {
    type: i0.ApplicationRef
  }], null);
})();
class ComponentStaticPool {
  _component;
  viewContainerRef;
  component;
  option;
  constructor(_component, viewContainerRef, component, option) {
    this._component = _component;
    this.viewContainerRef = viewContainerRef;
    this.component = component;
    this.option = option;
  }
  append(data) {
    return this._component.append(this.viewContainerRef, this.component, data, this.option);
  }
  clear() {
    this.viewContainerRef.clear();
  }
}
class ComponentPool {
  _component;
  viewContainerRef;
  option;
  constructor(_component, viewContainerRef, option) {
    this._component = _component;
    this.viewContainerRef = viewContainerRef;
    this.option = option;
  }
  append(component, data) {
    return this._component.append(this.viewContainerRef, component, data, this.option);
  }
}
function destroy(controls) {
  return controls.forEach(destroyMap);
}
function destroyMap(control) {
  control.destroy();
}
class DotResizeEventService {
  _documentElement;
  size$;
  width$;
  height$;
  constructor(document) {
    this._documentElement = document.documentElement;
    const size$ = new BehaviorSubject(this.getSize());
    this.size$ = size$;
    this.width$ = size$.pipe(map$1(size => size.width), distinctUntilChanged());
    this.height$ = size$.pipe(map$1(size => size.height), distinctUntilChanged());
    fromEvent(window, 'resize').subscribe(() => {
      size$.next(this.getSize());
    });
  }
  getSize() {
    const {
      clientWidth: width,
      clientHeight: height
    } = this._documentElement;
    return {
      width,
      height
    };
  }
  static ɵfac = function DotResizeEventService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotResizeEventService)(i0.ɵɵinject(DOCUMENT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotResizeEventService,
    factory: DotResizeEventService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotResizeEventService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: HTMLDocument,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }], null);
})();
class DotElementService {
  _resize;
  document;
  documentElement;
  constructor(_resize, document) {
    this._resize = _resize;
    this.document = document;
    this.documentElement = document.documentElement;
  }
  availableSize$(node) {
    node = getNode(node);
    return this._resize.size$.pipe(debounceTime(240), map$1(size => {
      let parent = node.parentNode;
      while (parent.nodeType === 1) {
        if (isNotInlineElement(parent)) {
          return {
            width: parent.scrollWidth,
            height: getHeight(parent, size.height)
          };
        }
        parent = parent.parentNode;
      }
      return size;
    }));
  }
  static ɵfac = function DotElementService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotElementService)(i0.ɵɵinject(DotResizeEventService), i0.ɵɵinject(DOCUMENT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotElementService,
    factory: DotElementService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotElementService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: DotResizeEventService
  }, {
    type: HTMLDocument,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }], null);
})();
function isNotInlineElement(node) {
  return getComputedStyle(node).getPropertyValue('display') !== 'inline';
}
function getHeight(node, maxHeight) {
  let parent = node;
  let grand;
  let height;
  let styleHeight;
  let usedPercent;
  while (parent.nodeType === 1) {
    height = parent.scrollHeight;
    styleHeight = parent.style['height'];
    if (usedPercent || styleHeight.endsWith('%')) {
      usedPercent = true;
      grand = parent.parentNode;
      if (grand.nodeType !== 1 || grand.style['height'] === '') {
        parent = grand;
        continue;
      }
    }
    if (isNotInlineElement(parent)) {
      if (height) {
        break;
      }
    }
    parent = parent.parentNode;
  }
  if (!height) {
    height = maxHeight;
  }
  return maxHeight;
}
class DotElementRefService {
  injector;
  document;
  _renderer;
  constructor(injector, document, rendererFactory) {
    this.injector = injector;
    this.document = document;
    this._renderer = rendererFactory.createRenderer(null, null);
  }
  static ɵfac = function DotElementRefService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotElementRefService)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(i0.RendererFactory2));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotElementRefService,
    factory: DotElementRefService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotElementRefService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i0.Injector
  }, {
    type: Document,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }, {
    type: i0.RendererFactory2
  }], null);
})();
class DotStyleService {
  _renderer;
  _document;
  constructor(_renderer, _document) {
    this._renderer = _renderer;
    this._document = _document;
  }
  set(element, property, value) {
    this._renderer.setStyle(getNode(element), property, value);
  }
  getVar(name) {
    return getVar(this._document.documentElement, name);
  }
  setVar(name, value) {
    setVar(this._document.documentElement, name, value);
  }
  _transform(node, method, ...values) {
    node = getNode(node);
    const {
      style
    } = node;
    let value = `${method}(${values.join(', ')})`;
    if (style.transform) {
      value += ' ' + style.transform.replace(new RegExp(`${method}[^(]*\\([^)]*\\)`), '');
    }
    return this.set(node, 'transform', value);
  }
  scale(node, value) {
    value = Math.round(value * 100) / 100;
    return this._transform(node, 'scale', value);
  }
  translate(node, x, y) {
    if (typeof x === 'number') {
      x = x + 'px';
    }
    if (typeof y === 'number') {
      y = y + 'px';
    }
    return this._transform(node, 'translate', x, y);
  }
  static ɵfac = function DotStyleService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotStyleService)(i0.ɵɵinject(DotRendererService), i0.ɵɵinject(DOCUMENT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotStyleService,
    factory: DotStyleService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotStyleService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: DotRendererService
  }, {
    type: Document,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }], null);
})();
class DotMoveService {
  _injector;
  constructor(_injector) {
    this._injector = _injector;
  }
  active(node, options) {
    return new ElementMover(this._injector, node, options);
  }
  static ɵfac = function DotMoveService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotMoveService)(i0.ɵɵinject(i0.Injector));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotMoveService,
    factory: DotMoveService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotMoveService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i0.Injector
  }], null);
})();
class ElementMover {
  _injector;
  constructor(_injector, _node, _options) {
    this._injector = _injector;
    _node = getNode(_node);
    this._node = _node;
    this._style = _injector.get(DotStyleService);
    const parentNode = _node.parentNode;
    if (_options) {
      if (Array.isArray(_options)) {
        this._targetNode = _node;
        this._x = +_options[0];
        this._y = +_options[1];
        this.to(this._x, this._y);
      } else {
        const {
          usePercent,
          parent,
          target,
          area
        } = _options;
        this.usePercent = usePercent;
        this._parentNode = parent ? getNode(parent) : parentNode;
        this._targetNode = target ? getNode(target) : _node;
        this._areaNode = area ? getNode(area) : _node;
        if (_options['position']) {
          const {
            position
          } = _options;
          this.to(position[0], position[1]);
        } else {
          const {
            x,
            y
          } = _options;
          if (x !== undefined && y !== undefined) {
            this._x = Number(x);
            this._y = Number(y);
            this.to(this._x, this._y);
          }
        }
        this._movable = _options['movable'] === undefined ? true : _options['movable'];
      }
    } else {
      this._targetNode = _node;
      this._areaNode = parentNode;
    }
    this._targetNode.style.transformOrigin = '0 0';
    this._setMoveByTranslate(0, 0);
    this._subscription.add(fromEvent(_node, 'click').subscribe(this._onClick.bind(this)));
    if (this._movable) {
      this._setEvent();
    }
  }
  usePercent;
  clicked$ = new Subject();
  moved$ = new Subject();
  _node;
  _parentNode;
  _areaNode;
  _style;
  _options;
  _targetNode;
  _movable = true;
  _move;
  _moved;
  _startX;
  _startY;
  _endX = 0;
  _endY = 0;
  _x = 0;
  _y = 0;
  _subscription = new Subscription();
  _eventSubscription;
  _leave$ = new Subject();
  _selfSetEvent = this._setEvent.bind(this);
  _selfOnMouseDown = this._onMouseDown.bind(this);
  _selfOnMouseMove = this._onMouseMove.bind(this);
  _selfOnLeave = this._onMouseLeave.bind(this);
  _selfToZero = this.to.bind(this, 0, 0);
  get x() {
    return this._x;
  }
  get y() {
    return this._y;
  }
  enable(flag = true) {
    if (flag) {
      if (!this._movable) {
        this._setEvent();
      }
    } else {
      if (this._movable) {
        this._reset();
      }
    }
    this._movable = flag;
  }
  disable(flag = true) {
    return this.enable(!flag);
  }
  _setEvent() {
    this._reset();
    const {
      _eventSubscription,
      _node
    } = this;
    _node.addEventListener('mousedown', this._selfOnMouseDown);
    _node.addEventListener('dblclick', this._selfToZero);
    _eventSubscription.add(this._leave$.subscribe(this._selfSetEvent));
  }
  _onClick(event) {
    this._leave$.next(true);
    if (this._moved) {
      // console.debug('element mover onclick: moved');
      if (this._movable) {
        this.moved$.next({
          x: this._x,
          y: this._y
        });
      }
      event.stopPropagation();
      // event.preventDefault();
      // return false;
    } else {
      // console.debug('element mover onclick: not moved -> click');
      this.clicked$.next();
    }
  }
  _onMouseDown(event) {
    // console.log('mouse down');
    this._eventSubscription.add(timer(120).subscribe(() => {
      this._onDelayedMouseDown(event);
    }));
    this._moved = false;
    event.stopPropagation();
  }
  _onDelayedMouseDown(event) {
    // console.log('mouse delayed mouse down');
    this._node.classList.add('_element-move');
    this._startX = event.x;
    this._startY = event.y;
    // this._targetNode.style.zIndex = '1';
    this._setStopEvent();
    event.stopPropagation();
  }
  _onMouseMove(event) {
    // console.log('mouse delayed mouse move');
    this._move = true;
    const {
      x,
      y
    } = event;
    this._setMoving(this._endX = x - this._startX, this._endY = y - this._startY);
    event.stopPropagation();
  }
  _onMouseLeave() {
    // console.log('leave', Math.random());
    this._leave$.next(true);
    this._node.classList.remove('_element-move');
    if (!this._move) {
      return;
    }
    this._move = false;
    this._moved = true;
    const {
      _x,
      _y,
      _endX,
      _endY,
      _parentNode
    } = this;
    if (this.usePercent) {
      const {
        scrollWidth,
        scrollHeight
      } = _parentNode;
      let x = (_x || 0) + _endX / scrollWidth * 100;
      let y = (_y || 0) + _endY / scrollHeight * 100;
      x = toFixedRange(x, 100);
      y = toFixedRange(y, 100);
      this._targetNode.style.left = x + '%';
      this._targetNode.style.top = y + '%';
      this._style.translate(this._targetNode, 0, 0);
      this._x = x;
      this._y = y;
    } else {
      this._x += _endX;
      this._y += _endY;
    }
    // this._targetNode.style.zIndex = 'inherit';
  }
  _setStopEvent() {
    const {
      _node
    } = this;
    const {
      _eventSubscription
    } = this;
    // _eventSubscription.add(fromEvent(_node, 'mouseup').subscribe(this._selfOnLeave));
    // _eventSubscription.add(fromEvent(_node, 'mouseleave').subscribe(this._selfOnLeave));
    // _eventSubscription.add(fromEvent(_node, 'mousemove').subscribe(this._selfOnMouseMove));
    _eventSubscription.add(fromEvent(document, 'mouseup').subscribe(this._selfOnLeave));
    _eventSubscription.add(fromEvent(document, 'mouseleave').subscribe(this._selfOnLeave));
    _eventSubscription.add(fromEvent(document, 'mousemove').subscribe(this._selfOnMouseMove));
  }
  position(x, y) {
    this._x = x;
    this._y = y;
    this._targetNode.style.left = x + '%';
    this._targetNode.style.top = y + '%';
  }
  _setMove(x, y) {
    if (this.usePercent) {
      this._targetNode.style.left = x + '%';
      this._targetNode.style.top = y + '%';
    } else {
      this._setMoveByTranslate(x, y);
    }
    this._setMove = this._setMoveByTranslate;
  }
  _setMoveByTranslate(x, y) {
    this._style.translate(this._targetNode, x, y);
  }
  _setMoving(x, y) {
    if (!this.usePercent) {
      x += this._x;
      y += this._y;
    }
    this._setMove(x, y);
  }
  to(x, y) {
    const getX = value => {
      if (typeof value === 'string') {
        if (value.endsWith('%')) {
          return this._parentNode.getBoundingClientRect().width * (+value.substring(0, value.length - 1) / 100);
        }
      }
      return value;
    };
    const getY = value => {
      if (typeof value === 'string') {
        if (value.endsWith('%')) {
          return this._parentNode.getBoundingClientRect().height * (+value.substring(0, value.length - 1) / 100);
        }
      }
      return value;
    };
    x = getX(x);
    y = getY(y);
    this._x = +x;
    this._y = +y;
    this._setMove(+x, +y);
    return this;
  }
  toX(x) {
    this._x = +x;
    this._setMove(this._x, this._y);
  }
  toY(y) {
    this._y = +y;
    this._setMove(this._x, this._y);
  }
  addX(x) {
    this._x += x;
    this._setMove(this._x, this._y);
  }
  addY(y) {
    this._y += y;
    this._setMove(this._x, this._y);
  }
  _reset() {
    if (this._eventSubscription) {
      this._eventSubscription.unsubscribe();
    }
    const {
      _node
    } = this;
    _node.removeEventListener('mousedown', this._selfOnMouseDown);
    _node.removeEventListener('dblclick', this._selfToZero);
    this._eventSubscription = new Subscription();
  }
  destroy() {
    this._reset();
  }
}
class DotOperationService {
  _actionMap = {};
  _actions = [];
  _delay = 1000;
  classList$ = new BehaviorSubject(null);
  changed$ = new BehaviorSubject(1);
  prepare = 0;
  load = 0;
  cancel = 0;
  action(type) {
    const {
      _actionMap
    } = this;
    if (!_actionMap[type]) {
      _actionMap[type] = new OperationAction(this, type);
      this._actions.push(_actionMap[type]);
    }
    return _actionMap[type];
  }
  _prepare() {
    this.prepare++;
    this._changed();
  }
  _prepared() {
    this.prepare--;
    if (!this.prepare) {
      this._changed();
    }
  }
  _load() {
    this.load++;
    this._changed();
  }
  _loaded() {
    setTimeout(() => {
      this.load--;
      if (!this.load) {
        this._changed();
      }
    }, this._delay);
  }
  _cancel() {
    this.cancel++;
    this._changed();
  }
  _canceled() {
    setTimeout(() => {
      this.cancel--;
      if (!this.cancel) {
        this._changed();
      }
    }, this._delay);
  }
  _changed() {
    const defaultClassList = [];
    let dirty = 0;
    if (this.prepare) {
      defaultClassList[0] = '_preparing';
    }
    if (this.load) {
      defaultClassList[defaultClassList.length] = '_loading';
    }
    if (this.cancel) {
      defaultClassList[defaultClassList.length] = '_canceling';
    }
    this.classList$.next(this._actions.reduce((classList, action) => {
      if (action.actionDirty) {
        dirty++;
        const {
          value
        } = action;
        classList[classList.length] = '_' + action.type + (action.actionError ? '_error' : value || value === 0 || value === false ? '_true' : '_false');
      }
      return classList;
    }, defaultClassList));
    if (dirty) {
      this.changed$.next(this.changed$.value + 1);
    }
  }
  static ɵfac = function DotOperationService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotOperationService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotOperationService,
    factory: DotOperationService.ɵfac
  });
}
__decorate([Debounce()], DotOperationService.prototype, "_changed", null);
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotOperationService, [{
    type: Injectable
  }], null, {
    _changed: []
  });
})();
class OperationAction extends BehaviorSubject {
  _operation;
  type;
  actionDirty;
  actionCancel;
  actionError;
  _actionLoad;
  _actionPrepare;
  _actionSubscription;
  get loadable() {
    return !this._actionPrepare && !this._actionLoad;
  }
  set subscription(subscription) {
    this._actionSubscription = checkSubscription(this._actionSubscription, subscription);
  }
  constructor(_operation, type) {
    super(null);
    this._operation = _operation;
    this.type = type;
  }
  prepare() {
    if (this._actionPrepare || this._actionLoad) {
      return false;
    }
    this._actionPrepare = true;
    this._operation._prepare();
    return this;
  }
  prepared() {
    if (!this._actionPrepare) {
      throw Error('Does not started Prepare');
    }
    this.actionError = false;
    this._actionPrepare = false;
    this._operation._prepared();
  }
  load() {
    if (this._actionPrepare || this._actionLoad) {
      return false;
    }
    this.actionDirty = true;
    this.actionError = false;
    this._actionLoad = true;
    this._operation._load();
    return true;
  }
  loaded() {
    if (!this._actionLoad) {
      throw Error('Does not started Prepare');
    }
    this._actionLoad = false;
    this._operation._loaded();
  }
  /**
   * Set Load State when not Loading
   * Set Cancel State when Loading
   */
  loadOrCancel() {
    if (this.load()) {
      return true;
    }
    this.cancel();
    return false;
  }
  error(reason) {
    if (this._actionPrepare) {
      this.prepared();
    }
    if (this._actionLoad) {
      this.loaded();
    }
    this.actionError = true;
    this._operation._changed();
  }
  cancel() {
    const {
      _actionPrepare,
      _actionLoad
    } = this;
    if (!_actionPrepare && !_actionLoad) {
      return;
    }
    const {
      _operation,
      _actionSubscription
    } = this;
    if (_actionPrepare) {
      this.prepared();
    }
    if (_actionLoad) {
      this.loaded();
    }
    this.actionError = false;
    if (_actionSubscription) {
      _operation._cancel();
      _actionSubscription.unsubscribe();
      _operation._canceled();
    } else {
      _operation._changed();
    }
  }
  next(value) {
    this.actionDirty = true;
    super.next(value);
    this._operation._changed();
  }
  clean() {
    this.actionDirty = false;
    super.next(undefined);
    this._operation._changed();
  }
  nextOrClean(value) {
    if (value) {
      this.next(value);
    } else {
      this.clean();
    }
  }
}
const hasValueMap = map$1(value => value > 0);
const distinctUntilChangedOperator = distinctUntilChanged();
class DotScrollEventService {
  _x;
  _y;
  _startY;
  _up;
  position$;
  x$;
  y$;
  scrollXOnly$;
  scrollYOnly$;
  scrolling$;
  scrolled$;
  xScrolled$;
  yScrolled$;
  up$;
  down$;
  constructor(document) {
    const position = this._getPosition();
    this._startY = position.y;
    const position$ = new BehaviorSubject(position);
    const scrolling$ = new Subject();
    this.position$ = position$;
    this.x$ = position$.pipe(map$1(position => position.x), distinctUntilChangedOperator);
    this.y$ = position$.pipe(map$1(position => position.y), distinctUntilChangedOperator);
    this.scrollXOnly$ = this.x$.pipe(filter(() => this._y === 0));
    this.scrollYOnly$ = this.y$.pipe(filter(() => this._x === 0));
    this.scrolling$ = scrolling$.pipe(distinctUntilChangedOperator);
    this.scrolled$ = position$.pipe(map$1(position => position.x > 0 || position.y > 0), distinctUntilChangedOperator);
    this.xScrolled$ = this.x$
    // pos => x
    // distinct
    .pipe(hasValueMap);
    this.yScrolled$ = this.y$
    // pos => y
    // distinct
    .pipe(hasValueMap);
    let upY = 0;
    let downY = 0;
    this.up$ = this.y$.pipe(map$1(y => {
      const flag = y && upY > y;
      upY = y;
      return flag;
    }), distinctUntilChangedOperator);
    this.down$ = this.y$.pipe(map$1(y => {
      const flag = y && downY < y;
      downY = y;
      return flag;
    }), distinctUntilChangedOperator);
    let scrollEndTimer;
    const scrollingEnd = () => {
      scrolling$.next(false);
    };
    fromEvent(document, 'scroll').subscribe(() => {
      position$.next(this._getPosition());
      scrolling$.next(true);
      scrollEndTimer = checkTimeout(scrollEndTimer, scrollingEnd, 640);
    });
  }
  _getPosition() {
    const x = window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0;
    const y = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this._x = x;
    this._y = y;
    return {
      x,
      y
    };
  }
  targetFrom(node) {
    let target = node;
    do {
      const isScroll = target['_dotScrollable'] === undefined ? (overflow => overflow === 'auto' || overflow === 'scroll')(getComputedStyle(target)['overflow']) : target['_dotScrollable'];
      if (isScroll) {
        target['_dotScrollable'] = true;
        return new DotScrollEvent(target);
      }
      target['_dotScrollable'] = false;
      target = target.parentNode;
    } while (target instanceof HTMLElement);
    return this;
  }
  static ɵfac = function DotScrollEventService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotScrollEventService)(i0.ɵɵinject(DOCUMENT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotScrollEventService,
    factory: DotScrollEventService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotScrollEventService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: HTMLDocument,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }], null);
})();
class DotScrollEvent {
  _node;
  _x;
  _y;
  position$;
  scrolling$;
  constructor(target) {
    this._node = target;
    const position$ = new BehaviorSubject(this._getPosition());
    const scrolling$ = new Subject();
    this.position$ = position$;
    this.scrolling$ = scrolling$.pipe(distinctUntilChanged());
    function scrollingEnd() {
      scrolling$.next(false);
    }
    fromEvent(target, 'scroll').subscribe(() => {
      position$.next(this._getPosition());
      scrolling$.next(true);
      timer(640).pipe(takeUntil(scrolling$)).subscribe(scrollingEnd);
    });
  }
  _getPosition() {
    const {
      scrollTop: x,
      scrollLeft: y
    } = this._node;
    this._x = x;
    this._y = y;
    return {
      x,
      y
    };
  }
}
class DotSignalService {
  _subjects$ = {};
  listen(name) {
    const {
      _subjects$
    } = this;
    let subject = _subjects$[name];
    if (!subject) {
      _subjects$[name] = subject = new Subject();
    }
    return subject;
  }
  change(name) {
    const {
      _subjects$
    } = this;
    let subject = _subjects$[name];
    if (!subject) {
      _subjects$[name] = subject = new Subject();
    }
    subject.next();
  }
  static ɵfac = function DotSignalService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotSignalService)();
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotSignalService,
    factory: DotSignalService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotSignalService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
class DotZoomService {
  _injector;
  constructor(_injector) {
    this._injector = _injector;
  }
  active(node, options) {
    return new ElementZoomer(this._injector, node, options);
  }
  static ɵfac = function DotZoomService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotZoomService)(i0.ɵɵinject(i0.Injector));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotZoomService,
    factory: DotZoomService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotZoomService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i0.Injector
  }], null);
})();
class ElementZoomer {
  _injector;
  _subscription = new Subscription();
  _style;
  _node;
  _targetNode;
  scaleWidth;
  scaleHeight;
  _disabled;
  _fitted;
  level = 1;
  amount = 0.1;
  width;
  height;
  options;
  changed$ = new Subject();
  constructor(_injector, node, options) {
    this._injector = _injector;
    node = getNode(node);
    this._node = node;
    this._style = _injector.get(DotStyleService);
    const {
      _subscription
    } = this;
    _subscription.add(fromEvent(node, 'mousewheel').subscribe(event => {
      if (this._disabled) {
        return;
      }
      if (event.deltaY < 0) {
        this.in();
      } else {
        this.out();
      }
      event.preventDefault();
    }));
    _subscription.add(fromEvent(node, 'dblclick').subscribe(this.fit.bind(this)));
    _subscription.add(fromEvent(window, 'resize').pipe(debounceTime(48)).subscribe(() => {
      if (this._fitted) {
        this.fit();
      }
    }));
    if (options) {
      this._targetNode = getNode(options['target'] || node);
      this.width = options.width;
      this.height = options.height;
      if (options.zoom) {
        this.level = parseFloat(options.zoom);
      }
      this.options = options;
    } else {
      this._targetNode = node;
    }
    this._setSize();
  }
  setLevel(level) {
    level = parseFloat(level).toFixed(2);
    if (level < 0) {
      level = this.amount;
    }
    this.level = level;
    this.changed$.next(level);
    this._setScaleSize();
    return this;
  }
  _setScaleSize() {
    const {
      level
    } = this;
    this.scaleWidth = this.width * level;
    this.scaleHeight = this.height * level;
    const {
      style
    } = this._targetNode;
    style.width = this.scaleWidth + 'px';
    style.height = this.scaleHeight + 'px';
  }
  _setSize() {
    const {
      _targetNode
    } = this;
    if (!this.width) {
      this.width = _targetNode.scrollWidth;
    }
    if (!this.height) {
      this.height = _targetNode.scrollHeight;
    }
    if (this.options.fit) {
      this.fit();
    }
    this._setScaleSize();
    this.align();
  }
  align() {
    const {
      _node,
      _targetNode
    } = this;
    const {
      width: parentWidth,
      height: parentHeight
    } = _node.getBoundingClientRect();
    const left = Math.round((parentWidth - this.scaleWidth) / 2);
    const top = Math.round((parentHeight - this.scaleHeight) / 2);
    _targetNode.style.left = left + 'px';
    _targetNode.style.top = top + 'px';
  }
  origin() {
    this._fitted = false;
    this.setLevel(1);
    this.align();
  }
  fit(event) {
    this._fitted = true;
    const {
      _node,
      width,
      height
    } = this;
    const {
      width: parentWidth,
      height: parentHeight
    } = _node.getBoundingClientRect();
    const widthRatio = parentWidth / width;
    const heightRatio = parentHeight / height;
    let level = 1;
    if (widthRatio > heightRatio) {
      level = heightRatio;
    } else {
      level = widthRatio;
    }
    this.setLevel(level);
    this.align();
    if (event) {
      event.preventDefault();
    }
  }
  in() {
    return this.setLevel(Number(this.level) + this.amount);
  }
  out() {
    return this.setLevel(Number(this.level) - this.amount);
  }
  disabled() {
    this._disabled = true;
  }
  destroy() {
    this._subscription.unsubscribe();
    this.changed$.complete();
  }
}
const DOT_CORE_MODULE_CONFIGURE = new InjectionToken('#ng-dot/core/configure');
const DOT_CORE_FEATURE_CONFIGURE = new InjectionToken('#ng-dot/core/feature-configure');
class DotCoreService {
  _injector;
  _features = new Map();
  configure;
  constructor(_injector, configure, featureConfigures = []) {
    this._injector = _injector;
    this.configure = Object.assign({
      workers: {},
      links: {}
    }, configure || {});
    let current = featureConfigures?.length;
    while (current-- > 0) {
      this.setFeature(featureConfigures[current]);
    }
  }
  /**
   * Set Feature without Module
   * @param configure
   */
  setFeature(configure) {
    this._features.set(configure.namespace, configure);
  }
  /**
   * Get boolean about exists of decoupled feature.
   *
   * @example
   * ```ts
   * @NgModule({
   * 	imports: [
   * 		CoreModule.forFeature('foo')
   * 	]
   * })
   * export class FooModule {
   * }
   *
   * @NgModule()
   * export class BarModule {
   * 	constructor(_core: DotCoreService){
   * 		if(_core.includes('foo')){
   * 			// Do something
   * 		}
   * 	}
   * }
   * ```
   * @param namespace
   */
  includes(namespace) {
    return this._features.has(namespace instanceof Identifier ? namespace.root : namespace);
  }
  import(namespace_or_identifier_or_childIdentifier, className) {
    const id = namespace_or_identifier_or_childIdentifier;
    let namespace = id;
    if (id instanceof ChildIdentifier) {
      namespace = id.parent;
      className = id.symbol;
    }
    if (!className) {
      className = namespace.toString();
    }
    const {
      _features
    } = this;
    if (!_features.has(namespace)) {
      throw new ReferenceError(`${namespace} is not a dynamic namespace`);
    }
    const feature = _features.get(namespace);
    const provider = feature.providers[className];
    if (!provider) {
      throw new ReferenceError(`${className.toString()} is not a dynamic provider of ${namespace}`);
    }
    return provider;
  }
  /**
   * Get Constructor from provider of decoupled feature as Promise
   *
   * @example
   *
   * ```ts
   * @Injectable({
   * 	providedIn: 'root'
   * })
   * export class FooService {
   * }
   * @NgModule({
   * 	imports: [
   * 		CoreModule.forFeature({
   * 			namespace: 'foo',
   * 			providers: {
   * 				'fooService': () => import('./foo.service').then(m => m.FooService)
   * 			}
   * 		})
   * 	]
   * })
   * export class FooModule {
   * }
   *
   * @NgModule()
   * export class BarModule {
   * 	constructor(_core: DotCoreService){
   * 		if(_core.includes('foo')){
   * 			_core.import$('foo', 'FooService')
   * 				.then(FooService => {
   *		 			const _foo = inject(_core.import('foo', 'FooService'));
   * 				});
   * 		}
   * 	}
   * }
   * ```
   * @param namespace
   * @param className
   */
  import$(namespace, className) {
    return promisify(this.import(namespace, className)());
  }
  inject(namespace_or_identifier_or_subIdentifier, className) {
    return this._injector.get(this.import(namespace_or_identifier_or_subIdentifier, className));
  }
  /**
   * Get Instance from provider of decoupled feature as Promise
   *
   * @example
   * ```ts
   * @Injectable({
   * 	providedIn: 'root'
   * })
   * export class FooService {
   * }
   * @NgModule({
   * 	imports: [
   * 		CoreModule.forFeature({
   * 			namespace: 'foo',
   * 			providers: {
   * 				'fooService': () => import('./foo.service').then(m => m.FooService)
   * 			}
   * 		})
   * 	]
   * })
   * export class FooModule {
   * }
   *
   * @NgModule()
   * export class BarModule {
   * 	constructor(_core: DotCoreService){
   * 		if(_core.includes('foo')){
   * 			_core.inject$('foo', 'FooService')
   * 			.then(_foo => {
   * 				// Do something
   * 			});
   * 		}
   * 	}
   * }
   * ```
   * @param namespace
   * @param className
   */
  inject$(namespace, className) {
    return this.import$(namespace, className).then(target => {
      return this._injector.get(target);
    });
  }
  /**
   * Apply `invoke` Function from provider of decoupled feature
   *
   * @example
   * ```ts
   * @Injectable({
   * 	providedIn: 'root'
   * })
   * export class FooService {
   * 	invoke(value: string){
   * 		console.log(value);
   * 	}
   * }
   * @NgModule({
   * 	imports: [
   * 		CoreModule.forFeature({
   * 			namespace: 'foo',
   * 			providers: {
   * 				'fooService': FooService
   * 			}
   * 		})
   * 	]
   * })
   * export class FooModule {
   * }
   *
   * @NgModule()
   * export class BarModule {
   * 	constructor(_core: DotCoreService){
   * 		if(_core.includes('foo')){
   * 			_core.apply('foo', 'FooService', ['Hello?']);
   * 		}
   * 	}
   * }
   * ```
   * @param namespace
   * @param className_or_args
   * @param args
   */
  apply(namespace, className_or_args, args) {
    let className = className_or_args;
    if (arguments.length < 3) {
      if (Array.isArray(className_or_args)) {
        className = null;
        args = className_or_args;
      }
    }
    const instance = this.inject(namespace, className);
    if ('invoke' in instance && typeof instance['invoke'] !== 'function') {
      throw new ReferenceError(`${namespace}/${className}.invoke is not a function`);
    }
    return instance['invoke'](...args);
  }
  /**
   * Get Worker Configured from DotCoreModule.forRoot
   * @param name
   */
  getWorker(name) {
    const worker = this.configure.workers[name];
    if (!worker) {
      throw new RangeError(`Cannot Found Worker ${name}`);
    }
    return worker();
  }
  static ɵfac = function DotCoreService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotCoreService)(i0.ɵɵinject(i0.Injector), i0.ɵɵinject(DOT_CORE_MODULE_CONFIGURE, 8), i0.ɵɵinject(DOT_CORE_FEATURE_CONFIGURE, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotCoreService,
    factory: DotCoreService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotCoreService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i0.Injector
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [DOT_CORE_MODULE_CONFIGURE]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [DOT_CORE_FEATURE_CONFIGURE]
    }]
  }], null);
})();
class DotTextComponent {
  set innerHTML(value) {
    if (this._init) {
      this.#setHTML();
    } else {
      this._html = value;
    }
  }
  set innerText(value) {
    if (this._init) {
      this.#setHTML();
    } else {
      this._text = value;
    }
  }
  set textContent(value) {
    this.innerText = value;
  }
  set contentEditable(value) {
    this._node.contentEditable = '' + value;
  }
  /**
   * Compatible with opened as modal
   */
  modalRef;
  _init;
  _html;
  _text;
  _node;
  constructor(elementRef) {
    this._node = elementRef.nativeElement;
  }
  ngOnInit() {
    if (this._text || this._html) {
      this.#setHTML();
    }
  }
  #setHTML() {
    if (this._text) {
      this._node.textContent = this._text;
    } else {
      this._node.innerHTML = this._html;
    }
  }
  static ɵfac = function DotTextComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotTextComponent)(i0.ɵɵdirectiveInject(i0.ElementRef));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DotTextComponent,
    selectors: [["ng-component"]],
    inputs: {
      innerHTML: "innerHTML",
      innerText: "innerText",
      textContent: "textContent",
      contentEditable: "contentEditable",
      modalRef: "modalRef"
    },
    decls: 0,
    vars: 0,
    template: function DotTextComponent_Template(rf, ctx) {},
    encapsulation: 2,
    changeDetection: 0
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotTextComponent, [{
    type: Component,
    args: [{
      standalone: true,
      template: ``,
      encapsulation: ViewEncapsulation.None,
      changeDetection: ChangeDetectionStrategy.OnPush
    }]
  }], () => [{
    type: i0.ElementRef
  }], {
    innerHTML: [{
      type: Input
    }],
    innerText: [{
      type: Input
    }],
    textContent: [{
      type: Input
    }],
    contentEditable: [{
      type: Input
    }],
    modalRef: [{
      type: Input
    }]
  });
})();
class DotColumnPipe {
  /**
   * @see getColumn
   * @param items
   * @param propertyName
   */
  transform(items, propertyName) {
    return getColumn(items, propertyName);
  }
  static ɵfac = function DotColumnPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotColumnPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "column",
    type: DotColumnPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotColumnPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'column'
    }]
  }], null, null);
})();

/**
 * Array.includes()
 */
class DotIncludesPipe {
  /**
   * @example
   * ```angular2html
   * <ng-container *ngIf="[1, 2, 3] | includes:1"></ng-container>
   * ```
   *
   * @param elements
   * @param searchElement
   */
  transform(elements, searchElement) {
    return elements && elements.indexOf(searchElement) > -1;
  }
  static ɵfac = function DotIncludesPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotIncludesPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "includes",
    type: DotIncludesPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotIncludesPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'includes'
    }]
  }], null, null);
})();
class DotJoinPipe {
  transform(value, glue = ', ') {
    return value.join(glue);
  }
  static ɵfac = function DotJoinPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotJoinPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "join",
    type: DotJoinPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotJoinPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'join'
    }]
  }], null, null);
})();
class DotLastPipe {
  transform(value) {
    return value[value.length - 1];
  }
  static ɵfac = function DotLastPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotLastPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "last",
    type: DotLastPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotLastPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'last'
    }]
  }], null, null);
})();
class DotKeysPipe {
  /**
   * Object.keys();
   * @param value
   */
  transform(value) {
    return Object.keys(value);
  }
  static ɵfac = function DotKeysPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotKeysPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "keys",
    type: DotKeysPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotKeysPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'keys'
    }]
  }], null, null);
})();
class DotValuesPipe {
  /**
   * Object.values()
   * @param value
   */
  transform(value) {
    return Object.values(value);
  }
  static ɵfac = function DotValuesPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotValuesPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "values",
    type: DotValuesPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotValuesPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'values'
    }]
  }], null, null);
})();
class DotEntriesPipe {
  /**
   * Object.entries()
   * @param value
   */
  transform(value) {
    return Object.entries(value);
  }
  static ɵfac = function DotEntriesPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotEntriesPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "entries",
    type: DotEntriesPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotEntriesPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'entries'
    }]
  }], null, null);
})();
class DotHasOwnPipe {
  transform(target, property) {
    return target && typeof target === 'object' && target.hasOwnProperty(property);
  }
  static ɵfac = function DotHasOwnPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotHasOwnPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "hasOwn",
    type: DotHasOwnPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotHasOwnPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'hasOwn'
    }]
  }], null, null);
})();
class DotPathWalkPipe {
  transform(value, path, defaultValue) {
    const result = pathWalk(value, path);
    return result === undefined ? defaultValue : result;
  }
  static ɵfac = function DotPathWalkPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotPathWalkPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "pathWalk",
    type: DotPathWalkPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotPathWalkPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'pathWalk'
    }]
  }], null, null);
})();
class DotExcludePipe {
  /**
   * Exclude Items from Array or Object
   * @param value
   * @param keys
   */
  transform(value, keys) {
    let current = keys.length;
    if (Array.isArray(value)) {
      const array = [...value];
      while (current-- > 0) {
        const key = keys[current];
        const index = array.indexOf(key);
        if (index > -1) {
          array.splice(index, 1);
        }
      }
      return array;
    }
    const object = {
      ...value
    };
    while (current-- > 0) {
      const key = keys[current];
      if (object[key] !== undefined) {
        delete object[key];
      }
    }
    return object;
  }
  static ɵfac = function DotExcludePipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotExcludePipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "exclude",
    type: DotExcludePipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotExcludePipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'exclude'
    }]
  }], null, null);
})();
class DotMinPipe {
  transform(a, b) {
    return arguments.length > 1 ? Math.min(a, b) : Math.min(...a);
  }
  static ɵfac = function DotMinPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotMinPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "min",
    type: DotMinPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotMinPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'min'
    }]
  }], null, null);
})();
class DotMaxPipe {
  transform(a, b) {
    return arguments.length === 2 ? Math.max(a, b) : Math.max(...a);
  }
  static ɵfac = function DotMaxPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotMaxPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "max",
    type: DotMaxPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotMaxPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'max'
    }]
  }], null, null);
})();
class DotRoundPipe {
  transform(value) {
    return Math.round(value);
  }
  static ɵfac = function DotRoundPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRoundPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "round",
    type: DotRoundPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRoundPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'round'
    }]
  }], null, null);
})();
class DotToFixedPipe {
  /**
   * Number.toFixed()
   * @param value
   * @param size
   */
  transform(value, size = 2) {
    return size && typeof value === 'number' ? value.toFixed(size) : value;
  }
  static ɵfac = function DotToFixedPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotToFixedPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "toFixed",
    type: DotToFixedPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotToFixedPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'toFixed'
    }]
  }], null, null);
})();
class DotRandomPipe {
  transform(...args) {
    return random.apply(this, args);
  }
  static ɵfac = function DotRandomPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRandomPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "random",
    type: DotRandomPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRandomPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'random'
    }]
  }], null, null);
})();
class DotToFitPipe {
  /**
   * Make same decimal point by size
   * @param value
   * @param size
   */
  transform(value, size = 2) {
    return size ? toFit(value, size) : value;
  }
  static ɵfac = function DotToFitPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotToFitPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "toFit",
    type: DotToFitPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotToFitPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'toFit'
    }]
  }], null, null);
})();
class DotAgePipe {
  /**
   * Make Korean Age from Date
   */
  transform(value) {
    const birthday = new Date(value);
    const now = new Date();
    let age = now.getFullYear() - birthday.getFullYear();
    if (now.getMonth() < birthday.getMonth() && now.getDate() < birthday.getDate()) {
      age--;
    }
    return age;
  }
  static ɵfac = function DotAgePipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotAgePipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "age",
    type: DotAgePipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotAgePipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'age'
    }]
  }], null, null);
})();
class DotDateBeforePipe {
  transform(target, time) {
    return Date.now() - (target instanceof Date ? target : new Date(target)).getTime() < time;
  }
  static ɵfac = function DotDateBeforePipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotDateBeforePipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "dateBefore",
    type: DotDateBeforePipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotDateBeforePipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'dateBefore'
    }]
  }], null, null);
})();
class DotNl2brPipe {
  transform(value) {
    return value ? value.replace(/(\n\r?|\r\n?)/g, '<br>') : '';
  }
  static ɵfac = function DotNl2brPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotNl2brPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "nl2br",
    type: DotNl2brPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotNl2brPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'nl2br'
    }]
  }], null, null);
})();
class DotRenderDatePipe {
  /**
   * @see renderDate
   * @param value
   */
  transform(value) {
    return renderDate(value);
  }
  static ɵfac = function DotRenderDatePipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRenderDatePipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "renderDate",
    type: DotRenderDatePipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRenderDatePipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'renderDate'
    }]
  }], null, null);
})();
class DotRenderHostPipe {
  /**
   * @see import('@js-dot/ui').renderHost
   * @param value
   */
  transform(value) {
    return value && typeof value === 'string' ? renderHost(value) : value;
  }
  static ɵfac = function DotRenderHostPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRenderHostPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "renderHost",
    type: DotRenderHostPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRenderHostPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'renderHost'
    }]
  }], null, null);
})();
class DotRenderTelPipe {
  /**
   *
   * @see renderTel
   * @param value
   */
  transform(value) {
    return renderTel(value);
  }
  static ɵfac = function DotRenderTelPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRenderTelPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "renderTel",
    type: DotRenderTelPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRenderTelPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'renderTel'
    }]
  }], null, null);
})();
const CAPITAL = /([A-Z])/g;
class DotCamel2brPipe {
  transform(value) {
    return value.replace(CAPITAL, '<br>$1');
  }
  static ɵfac = function DotCamel2brPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotCamel2brPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "camel2br",
    type: DotCamel2brPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotCamel2brPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'camel2br'
    }]
  }], null, null);
})();
class DotNonKrPipeStyleService extends Styles {
  constructor(document) {
    super(document.head);
    this.add({
      '.non-kr': {
        letterSpacing: '0'
      }
    });
  }
  static ɵfac = function DotNonKrPipeStyleService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotNonKrPipeStyleService)(i0.ɵɵinject(DOCUMENT));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotNonKrPipeStyleService,
    factory: DotNonKrPipeStyleService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotNonKrPipeStyleService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: Document,
    decorators: [{
      type: Inject,
      args: [DOCUMENT]
    }]
  }], null);
})();
class DotNonKrPipe {
  constructor(_style) {}
  transform(value) {
    return renderNonKr(value);
  }
  static ɵfac = function DotNonKrPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotNonKrPipe)(i0.ɵɵdirectiveInject(DotNonKrPipeStyleService, 16));
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "nonKr",
    type: DotNonKrPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotNonKrPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'nonKr'
    }]
  }], () => [{
    type: DotNonKrPipeStyleService
  }], null);
})();
class DotOlPipe {
  transform(items) {
    return toWrapString(items, {
      firstStarts: '<ol>',
      starts: '<li>',
      ends: '</li>',
      lastEnds: '</ul>'
    });
  }
  static ɵfac = function DotOlPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotOlPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "ol",
    type: DotOlPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotOlPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'ol'
    }]
  }], null, null);
})();
class DotParagraphPipe {
  transform(value) {
    return renderParagraph(value);
  }
  static ɵfac = function DotParagraphPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotParagraphPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "paragraph",
    type: DotParagraphPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotParagraphPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'paragraph'
    }]
  }], null, null);
})();
class DotUlPipe {
  transform(items) {
    return toWrapString(items, {
      firstStarts: '<ul>',
      starts: '<li>',
      ends: '</li>',
      lastEnds: '</ul>'
    });
  }
  static ɵfac = function DotUlPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotUlPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "ul",
    type: DotUlPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotUlPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'ul'
    }]
  }], null, null);
})();
class DotBase64ToHexPipe {
  /**
   * Make Base64 string to Hex string
   * @param value
   */
  transform(value) {
    return base64ToHex(value);
  }
  static ɵfac = function DotBase64ToHexPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotBase64ToHexPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "base64ToHex",
    type: DotBase64ToHexPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotBase64ToHexPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'base64ToHex'
    }]
  }], null, null);
})();
class DotByteFormatPipe {
  /**
   * Add Byte Units
   * @param bytes
   * @param decimals
   */
  transform(bytes, decimals = 2) {
    return toByteFormat(bytes, decimals);
  }
  static ɵfac = function DotByteFormatPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotByteFormatPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "byteFormat",
    type: DotByteFormatPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotByteFormatPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'byteFormat'
    }]
  }], null, null);
})();
class DotEllipsisPipe {
  /**
   * Add suffix as '...' when length great than size
   * @param value
   * @param size
   */
  transform(value, size = 100) {
    if (!value || value.length < size) {
      return value;
    }
    return value.substring(0, size - 3) + '...';
  }
  static ɵfac = function DotEllipsisPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotEllipsisPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "ellipsis",
    type: DotEllipsisPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotEllipsisPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'ellipsis'
    }]
  }], null, null);
})();
class DotFeatureIncludesPipe {
  _core;
  constructor(_core) {
    this._core = _core;
  }
  transform(namespace) {
    return this._core.includes(namespace);
  }
  static ɵfac = function DotFeatureIncludesPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotFeatureIncludesPipe)(i0.ɵɵdirectiveInject(DotCoreService, 16));
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "DotFeatureIncludesPipe",
    type: DotFeatureIncludesPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotFeatureIncludesPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'DotFeatureIncludesPipe'
    }]
  }], () => [{
    type: DotCoreService
  }], null);
})();
class DotRemoveHtmlPipe {
  /**
   * Remove HTML Element Strings
   * @param value
   * @param replace
   */
  transform(value, replace) {
    return stripTag(value, replace);
  }
  static ɵfac = function DotRemoveHtmlPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRemoveHtmlPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "removeHTML",
    type: DotRemoveHtmlPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRemoveHtmlPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'removeHTML'
    }]
  }], null, null);
})();
class DotToNumberPipe {
  /**
   * parseFloat()
   * @param value
   */
  transform(value) {
    return typeof value === 'boolean' ? value ? 1 : 0 : parseFloat(value);
  }
  static ɵfac = function DotToNumberPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotToNumberPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "toNumber",
    type: DotToNumberPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotToNumberPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'toNumber'
    }]
  }], null, null);
})();
class DotToPluralPipe {
  /**
   * Make Word to Plural Expression
   * @param value
   * @param size
   */
  transform(value, size) {
    return getPlural(value, size);
  }
  static ɵfac = function DotToPluralPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotToPluralPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "toPlural",
    type: DotToPluralPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotToPluralPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'toPlural'
    }]
  }], null, null);
})();
class DotToStringPipe {
  transform(value_or_object, ...properties) {
    const target = value_or_object;
    if (properties) {
      if (target) {
        let current = properties.length;
        while (current-- > 0) {
          const property = properties[current];
          target[property] = toString(target[property]);
        }
      }
      return target;
    }
    return toString(target);
  }
  static ɵfac = function DotToStringPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotToStringPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "toString",
    type: DotToStringPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotToStringPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'toString'
    }]
  }], null, null);
})();
function toString(value) {
  return value || value === false || value === 0 ? typeof value === 'string' ? value : value.toString() : value;
}
class DotTrustHtmlPipe {
  sanitizer;
  constructor(sanitizer) {
    this.sanitizer = sanitizer;
  }
  /**
   * Make XSS Safety HTML
   * @param html
   */
  transform(html) {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
  static ɵfac = function DotTrustHtmlPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotTrustHtmlPipe)(i0.ɵɵdirectiveInject(i1.DomSanitizer, 16));
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "trustHTML",
    type: DotTrustHtmlPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotTrustHtmlPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'trustHTML'
    }]
  }], () => [{
    type: i1.DomSanitizer
  }], null);
})();
class DotTrustUrlPipe {
  sanitizer;
  constructor(sanitizer) {
    this.sanitizer = sanitizer;
  }
  /**
   * Make XSS Safety URL
   * @param url
   */
  transform(url) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
  static ɵfac = function DotTrustUrlPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotTrustUrlPipe)(i0.ɵɵdirectiveInject(i1.DomSanitizer, 16));
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "trustUrl",
    type: DotTrustUrlPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotTrustUrlPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'trustUrl'
    }]
  }], () => [{
    type: i1.DomSanitizer
  }], null);
})();
class DotTypeOfPipe {
  transform(value) {
    return typeof value;
  }
  static ɵfac = function DotTypeOfPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotTypeOfPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "typeOf",
    type: DotTypeOfPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotTypeOfPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'typeOf'
    }]
  }], null, null);
})();
class DotParseIntPipe {
  transform(value) {
    return parseInt(value, 10);
  }
  static ɵfac = function DotParseIntPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotParseIntPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "parseInt",
    type: DotParseIntPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotParseIntPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'parseInt'
    }]
  }], null, null);
})();
class DotPromisifyPipe {
  transform(value) {
    return promisify(value);
  }
  static ɵfac = function DotPromisifyPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotPromisifyPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "promisify",
    type: DotPromisifyPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotPromisifyPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'promisify'
    }]
  }], null, null);
})();
class DotIsArrayPipe {
  /**
   * Get Boolean about value type is array
   * @param value
   */
  transform(value) {
    return Array.isArray(value);
  }
  static ɵfac = function DotIsArrayPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotIsArrayPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "isArray",
    type: DotIsArrayPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotIsArrayPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'isArray'
    }]
  }], null, null);
})();
class DotIsNumberPipe {
  /**
   * Returns number tested boolean
   * @param value
   */
  transform(value) {
    return isNumber(value);
  }
  static ɵfac = function DotIsNumberPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotIsNumberPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "isNumber",
    type: DotIsNumberPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotIsNumberPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'isNumber'
    }]
  }], null, null);
})();
class DotIsStringPipe {
  /**
   * Get Boolean about value is string type
   * @param value
   */
  transform(value) {
    return typeof value === 'string';
  }
  static ɵfac = function DotIsStringPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotIsStringPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "isString",
    type: DotIsStringPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotIsStringPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'isString'
    }]
  }], null, null);
})();
class DotToBooleanPipe {
  /**
   * Make Boolean
   * @param value
   */
  transform(value) {
    switch (typeof value) {
      case 'boolean':
        return value;
      case 'number':
        return value > 0;
      case 'string':
        return value !== '0' && value !== 'false';
    }
    return !!value;
  }
  static ɵfac = function DotToBooleanPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotToBooleanPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "toBoolean",
    type: DotToBooleanPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotToBooleanPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'toBoolean'
    }]
  }], null, null);
})();
class DotStyleVarPipe {
  _style;
  constructor(_style) {
    this._style = _style;
  }
  transform(name) {
    return this._style.getVar(name);
  }
  static ɵfac = function DotStyleVarPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotStyleVarPipe)(i0.ɵɵdirectiveInject(DotStyleService, 16));
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "styleVar",
    type: DotStyleVarPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotStyleVarPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'styleVar'
    }]
  }], () => [{
    type: DotStyleService
  }], null);
})();
class DotRgbPipe {
  transform(gradient, value) {
    return gradient.getRgb(value);
  }
  static ɵfac = function DotRgbPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRgbPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "rgb",
    type: DotRgbPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRgbPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'rgb'
    }]
  }], null, null);
})();
class DotUrlToAnchorPipe {
  /**
   * @see renderUrlToLink
   * @param value
   */
  transform(value) {
    return renderUrlToLink(value);
  }
  static ɵfac = function DotUrlToAnchorPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotUrlToAnchorPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "urlToAnchor",
    type: DotUrlToAnchorPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotUrlToAnchorPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'urlToAnchor'
    }]
  }], null, null);
})();
class DotToLocaleStringPipe {
  /**
   * Returns Localized String
   * @see Number.prototype.toLocaleString()
   * @see Date.prototype.toLocaleString()
   *
   * @param value toLocaleString-ify
   */
  transform(value) {
    return isNone(value) ? value : value.toLocaleString();
  }
  static ɵfac = function DotToLocaleStringPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotToLocaleStringPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "toLocaleString",
    type: DotToLocaleStringPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotToLocaleStringPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'toLocaleString'
    }]
  }], null, null);
})();
class DotNumberPassPipe {
  /**
   * Returns *ngIf condition with number that even a (number)0 to passable using (string)0
   * @param value
   */
  transform(value) {
    return typeof value === 'number' ? value === 0 ? '0' : value : null;
  }
  static ɵfac = function DotNumberPassPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotNumberPassPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "numberPass",
    type: DotNumberPassPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotNumberPassPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'numberPass'
    }]
  }], null, null);
})();
class DotToWrapStringPipe {
  /**
   * @see toWrapString
   * @param items
   * @param starts_or_option
   * @param ends
   * @param separator
   */
  transform(items, starts_or_option, ends, separator) {
    return toWrapString.apply(this, arguments);
  }
  static ɵfac = function DotToWrapStringPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotToWrapStringPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "toWrapString",
    type: DotToWrapStringPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotToWrapStringPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'toWrapString'
    }]
  }], null, null);
})();
class DotObservifyPipe {
  transform(value) {
    return observify(value);
  }
  static ɵfac = function DotObservifyPipe_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotObservifyPipe)();
  };
  static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
    name: "observify",
    type: DotObservifyPipe,
    pure: true
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotObservifyPipe, [{
    type: Pipe,
    args: [{
      standalone: true,
      name: 'observify'
    }]
  }], null, null);
})();
class DotBlurDirective {
  _node;
  constructor(elementRef) {
    this._node = elementRef.nativeElement;
  }
  onFocus() {
    this._node.blur();
  }
  static ɵfac = function DotBlurDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotBlurDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DotBlurDirective,
    selectors: [["", "dotBlur", ""]],
    hostBindings: function DotBlurDirective_HostBindings(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵlistener("focus", function DotBlurDirective_focus_HostBindingHandler() {
          return ctx.onFocus();
        });
      }
    }
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotBlurDirective, [{
    type: Directive,
    args: [{
      standalone: true,
      selector: '[dotBlur]'
    }]
  }], () => [{
    type: i0.ElementRef
  }], {
    onFocus: [{
      type: HostListener,
      args: ['focus']
    }]
  });
})();
class DotClassDirective {
  _render;
  set fromSelector(classes) {
    this.classes = classes;
    this._set();
  }
  classes;
  _classes;
  _host;
  // protected _classNameHolders: SelectorHold[];
  _subscription;
  _holders;
  constructor(_render, hostRef) {
    this._render = _render;
    this._host = hostRef.nativeElement;
  }
  ngOnInit() {
    this._set();
  }
  ngOnDestroy() {
    this._reset();
  }
  ngOnChanges(changes) {
    for (let key in changes) {
      if (!changes.hasOwnProperty(key)) {
        continue;
      }
      const change = changes[key];
      this[key] = change.currentValue;
      switch (key) {
        case 'classes':
          console.log('change render');
          this._set();
      }
    }
  }
  _reset() {
    if (this._holders) {
      this._holders.forEach(holder => {
        holder.pop();
      });
    }
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }
  _set() {
    this._reset();
    const {
      classes
    } = this;
    if (classes instanceof Observable) {
      this._subscription = classes.subscribe(_classes => {
        this._classes = _classes;
        this._update();
      });
    } else {
      this._classes = classes;
      this._update();
    }
  }
  _update() {
    this._holders = [];
    this._render.setProperty(this._host, 'className', '');
    const {
      _classes
    } = this;
    if (!_classes) {
      return;
    }
    this._setClass(_classes);
  }
  _setClass(targets) {
    const {
      _holders,
      _host,
      _render
    } = this;
    if (Array.isArray(targets)) {
      let current = targets.length;
      let target;
      while (current-- > 0) {
        target = targets[current];
        if (!target) {
          continue;
        }
        if (Array.isArray(target)) {
          this._setClass(target);
          continue;
        }
        switch (typeof target) {
          case 'string':
            _render.addClass(_host, target);
            break;
          case 'object':
            if (target instanceof Observable) {
              _holders[_holders.length] = new NgClassSwitch(_host).set(target);
              continue;
            }
            this._setClass(target);
        }
      }
    } else if (typeof targets === 'object') {
      let target;
      for (let className in targets) {
        if (!targets.hasOwnProperty(className)) {
          continue;
        }
        target = targets[className];
        switch (typeof target) {
          case 'boolean':
          case 'string':
          case 'number':
            if (target) {
              _render.addClass(_host, className);
            }
            continue;
          case 'object':
            if (target instanceof Observable) {
              _holders[_holders.length] = new NgClassSwitch(_host).set(target);
              continue;
            }
            if (target) {
              _render.addClass(_host, className);
            }
        }
      }
    }
  }
  static ɵfac = function DotClassDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotClassDirective)(i0.ɵɵdirectiveInject(DotRendererService), i0.ɵɵdirectiveInject(i0.ElementRef));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DotClassDirective,
    selectors: [["", "dotClass", ""]],
    inputs: {
      fromSelector: [0, "dotClass", "fromSelector"],
      classes: "classes"
    },
    features: [i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotClassDirective, [{
    type: Directive,
    args: [{
      standalone: true,
      selector: '[dotClass]'
    }]
  }], () => [{
    type: DotRendererService
  }, {
    type: i0.ElementRef
  }], {
    fromSelector: [{
      type: Input,
      args: ['dotClass']
    }],
    classes: [{
      type: Input
    }]
  });
})();
class DotMovableDirective {
  constructor(elementRef, _move) {
    _move.active(elementRef);
  }
  static ɵfac = function DotMovableDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotMovableDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(DotMoveService));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DotMovableDirective,
    selectors: [["", "dotMovable", ""]]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotMovableDirective, [{
    type: Directive,
    args: [{
      standalone: true,
      selector: '[dotMovable]'
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: DotMoveService
  }], null);
})();
class DotZoomableDirective {
  constructor(elementRef) {
    this._node = elementRef.nativeElement;
    this._setEvent();
  }
  _node;
  zoom = 1;
  _setEvent() {
    const {
      _node
    } = this;
    fromEvent(_node, 'mousewheel').subscribe(event => {
      console.log(event.deltaY);
    });
  }
  static ɵfac = function DotZoomableDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotZoomableDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DotZoomableDirective,
    selectors: [["", "dotZoomable", ""]],
    inputs: {
      zoom: "zoom"
    }
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotZoomableDirective, [{
    type: Directive,
    args: [{
      standalone: true,
      selector: '[dotZoomable]'
    }]
  }], () => [{
    type: i0.ElementRef
  }], {
    zoom: [{
      type: Input
    }]
  });
})();

/**
 * Class Setter that Replace [class] and [ngClass]
 * @description
 * [class] seems has to low rendering priority.
 * it's not work with many change detections at sometimes, but pure code was working fine.
 * this directive resolve it and apply more pattern of conditions
 */
class DotLazyClassDirective {
  _classOperators;
  _node;
  /**
   * Set [class] as Lazy
    * @example
   * Set Class From Observable:
   * ```angular
   * <div [dotLazyClass]="Observable<string>"></div>
   * ````
   *
   * Set 'whenTrue' From Observable<Boolean> when true:
   * ```angular
   * <div [dotLazyClass]="[Observable<any>, 'whenTrue']"></div>
   * ````
   *
   * Set 'whenTrue' when true, 'whenFalse' when false From Observable<Boolean>:
   * ```angular
   * <div [dotLazyClass]="[Observable<any>, 'whenTrue:whenFalse']"></div>
   * ````
   *
   * Set 'whenTrue' when false, 'whenFalse' when true From Observable<Boolean>:
   * ```angular
   * <div [dotLazyClass]="[Observable<any>, '!whenTrue:whenFalse']"></div>
   * ````
   *
   * Set Multiple Conditions
   * ```angular
   * <div [dotLazyClass]="{foo: Observable<any>, '!bar': Observable<any>}"></div>
   * ```
   * @param conditions
   */
  set fromSelector(conditions) {
    if (!conditions) {
      return;
    }
    this.conditions = conditions;
    this._render();
  }
  conditions;
  constructor(elementRef) {
    this._node = elementRef.nativeElement;
  }
  ngOnInit() {
    if (this.conditions) {
      this._render();
    }
  }
  ngOnChanges(changes) {
    for (let key in changes) {
      if (!changes.hasOwnProperty(key)) {
        continue;
      }
      const change = changes[key];
      const value = change.currentValue;
      this[key] = value;
      if (key === 'conditions') {
        this._render();
      }
    }
  }
  ngOnDestroy() {
    const {
      _classOperators
    } = this;
    if (_classOperators) {
      let current = _classOperators.length;
      while (--current > 0) {
        _classOperators[current].pop();
        delete _classOperators[current];
      }
    }
  }
  //@Throttle()
  _render() {
    this.ngOnDestroy();
    const {
      conditions,
      _node
    } = this;
    const formalizedConditions = Array.isArray(conditions) ? [conditions] : valueEntries(conditions);
    let current = formalizedConditions.length;
    const classOperators = [];
    while (current-- > 0) {
      const condition = formalizedConditions[current];
      let subject = condition[0];
      if (Array.isArray(subject)) {
        const path = subject[1];
        if (path) {
          subject = subject[0].pipe(map$1(response => pathWalk(response, path)));
        }
      }
      let classNames = condition[1];
      const invert = classNames && classNames.startsWith('!');
      if (invert) {
        classNames = classNames.slice(1);
      }
      const [whenTrue, whenFalse] = (classNames || '').split(':');
      let classOperator;
      if (whenTrue) {
        classOperator = new NgClassToggle(_node, whenTrue, whenFalse);
        subject = subject.pipe(map$1(invert ? falseMapFunction : trueMapFunction));
      } else {
        classOperator = new NgClassSwitch(_node);
      }
      classOperator.set(subject);
      classOperators[current] = classOperator;
    }
    this._classOperators = classOperators;
  }
  static ɵfac = function DotLazyClassDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotLazyClassDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DotLazyClassDirective,
    selectors: [["", "dotLazyClass", ""]],
    inputs: {
      fromSelector: [0, "dotLazyClass", "fromSelector"],
      conditions: "conditions"
    },
    features: [i0.ɵɵNgOnChangesFeature]
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotLazyClassDirective, [{
    type: Directive,
    args: [{
      standalone: true,
      selector: '[dotLazyClass]'
    }]
  }], () => [{
    type: i0.ElementRef
  }], {
    fromSelector: [{
      type: Input,
      args: ['dotLazyClass']
    }],
    conditions: [{
      type: Input
    }]
  });
})();
function valueEntries(object) {
  const result = [];
  for (let key in object) {
    if (object.hasOwnProperty(key)) {
      result[key] = [object[key], key];
    }
  }
  return result;
}
function trueMapFunction(response) {
  return !!casting(response);
}
function falseMapFunction(response) {
  return !casting(response);
}
function casting(value) {
  if (isNumeric(value)) {
    return +value;
  }
  return value;
}
class DotHtmlDirective {
  set fromSelector(html) {
    this._node.innerHTML = html;
  }
  _node;
  constructor(elementRef) {
    this._node = elementRef.nativeElement;
  }
  static ɵfac = function DotHtmlDirective_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotHtmlDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
  };
  static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
    type: DotHtmlDirective,
    selectors: [["", "dotHtml", ""]],
    inputs: {
      fromSelector: [0, "dotHtml", "fromSelector"]
    }
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotHtmlDirective, [{
    type: Directive,
    args: [{
      standalone: true,
      selector: '[dotHtml]'
    }]
  }], () => [{
    type: i0.ElementRef
  }], {
    fromSelector: [{
      type: Input,
      args: ['dotHtml']
    }]
  });
})();

/**
 * Dynamic Component Renderer
 */
class DotComponent {
  _component;
  _viewContainerRef;
  option;
  data;
  click = new EventEmitter();
  _instanceRef;
  _data;
  #takeUntilDestroy = takeUntilDestroyed();
  constructor(_component, _viewContainerRef) {
    this._component = _component;
    this._viewContainerRef = _viewContainerRef;
  }
  ngOnInit() {
    this.#setInputs();
  }
  ngOnChanges(changes) {
    for (let property in changes) {
      if (!changes.hasOwnProperty(property)) {
        continue;
      }
      this[property] = changes[property].currentValue;
    }
    if ('data' in changes) {
      this.#setInputs();
    }
  }
  #setInputs() {
    const {
      option,
      data
    } = this;
    const {
      dataAs,
      dataTo,
      inputs
    } = option;
    let mappedData = {
      ...(inputs || {})
    };
    if (dataAs) {
      if (Array.isArray(dataAs)) {
        let current = dataAs.length;
        while (current-- > 0) {
          const name = dataAs[current];
          mappedData[name] = data[name];
        }
      } else {
        for (const name in dataAs) {
          if (!dataAs.hasOwnProperty(name)) {
            continue;
          }
          mappedData[name] = pathWalk(data, dataAs[name]);
        }
      }
    } else if (dataTo) {
      mappedData[dataTo] = data;
    } else if (!inputs && data && typeof data === 'object') {
      mappedData = data;
    }
    this._data = mappedData;
    if (option.loadComponent['.componentClass']) {
      this.#append(option.loadComponent['.componentClass']);
    } else {
      promisify(option.loadComponent()).then(componentClass => {
        option.loadComponent['.componentClass'] = componentClass;
        this.#append(componentClass);
      });
    }
  }
  #append(component) {
    this.#destroy();
    const instanceRef = this._component.append(this._viewContainerRef, component, this._data);
    this._instanceRef = instanceRef;
    const {
      instance
    } = instanceRef;
    if (instance['click'] instanceof EventEmitter) {
      instance['click'].pipe(this.#takeUntilDestroy).subscribe(this.option.click ? $event => {
        this.option.click($event);
        this.click.emit($event);
      } : $event => {
        this.click.emit($event);
      });
    }
  }
  #destroy() {
    if (this._instanceRef) {
      this._instanceRef.destroy();
    }
  }
  ngOnDestroy() {
    this.#destroy();
  }
  static ɵfac = function DotComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotComponent)(i0.ɵɵdirectiveInject(DotComponentService), i0.ɵɵdirectiveInject(i0.ViewContainerRef));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DotComponent,
    selectors: [["dot-component"], ["", "dot-component", ""]],
    inputs: {
      option: "option",
      data: "data"
    },
    outputs: {
      click: "click"
    },
    features: [i0.ɵɵNgOnChangesFeature],
    decls: 0,
    vars: 0,
    template: function DotComponent_Template(rf, ctx) {},
    encapsulation: 2,
    changeDetection: 0
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotComponent, [{
    type: Component,
    args: [{
      standalone: true,
      selector: 'dot-component, [dot-component]',
      template: ``,
      encapsulation: ViewEncapsulation.None,
      changeDetection: ChangeDetectionStrategy.OnPush
    }]
  }], () => [{
    type: DotComponentService
  }, {
    type: i0.ViewContainerRef
  }], {
    option: [{
      type: Input
    }],
    data: [{
      type: Input
    }],
    click: [{
      type: Output
    }]
  });
})();
class DotCoreModule {
  static forRoot(configure) {
    return {
      ngModule: DotCoreModule,
      providers: [{
        provide: DOT_CORE_MODULE_CONFIGURE,
        useValue: configure
      }]
    };
  }
  static forFeature(namespace_or_configure) {
    return {
      ngModule: DotCoreModule,
      providers: [{
        provide: DOT_CORE_FEATURE_CONFIGURE,
        useValue: typeof namespace_or_configure === 'string' || namespace_or_configure instanceof Identifier ? {
          namespace: namespace_or_configure
        } : namespace_or_configure,
        multi: true
      }]
    };
  }
  static ɵfac = function DotCoreModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotCoreModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: DotCoreModule
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotCoreModule, [{
    type: NgModule
  }], null, null);
})();

//////////////////////////////
// Deprecates
// import * as JS_CORE from '@js-dot/core';
// import {movedTo} from '@js-dot/dev/compatible';
// import * as JS_RX from '@js-dot/rx';
//
// /**
//  * @deprecated
//  * @see JS_CORE.Debounce
//  */
// export const Debounce          = movedTo(JS_CORE.Debounce);
// /**
//  * @deprecated
//  * @see JS_CORE.Throttle
//  */
// export const Throttle          = movedTo(JS_CORE.Throttle);
// /**
//  * @deprecated
//  * @see JS_CORE.WithLocal
//  */
// export const FromLocal         = movedTo(JS_CORE.WithLocal, 'FromLocal');
// /**
//  * @deprecated
//  * @see JS_CORE.WithLocal
//  */
// export const FormSession       = movedTo(JS_CORE.WithLocal, 'FromSession');
// /**
//  * @deprecated
//  * @see JS_RX.checkSubscription
//  */
// export const checkSubscription = movedTo(JS_RX.checkSubscription);

/**
 * Generated bundle index. Do not edit.
 */

export { ComponentPool, ComponentStaticPool, DOT_CORE_FEATURE_CONFIGURE, DOT_CORE_MODULE_CONFIGURE, DotAgePipe, DotAssetService, DotBase64ToHexPipe, DotBlurDirective, DotByteFormatPipe, DotCamel2brPipe, DotClassDirective, DotColumnPipe, DotComponent, DotComponentService, DotCoreModule, DotCoreService, DotDateBeforePipe, DotElementRefService, DotElementService, DotEllipsisPipe, DotEntriesPipe, DotExcludePipe, DotFeatureIncludesPipe, DotHasOwnPipe, DotHtmlDirective, DotIncludesPipe, DotIsArrayPipe, DotIsNumberPipe, DotIsStringPipe, DotJoinPipe, DotKeysPipe, DotLastPipe, DotLazyClassDirective, DotMaxPipe, DotMinPipe, DotMovableDirective, DotMoveService, DotNl2brPipe, DotNonKrPipe, DotNonKrPipeStyleService, DotNumberPassPipe, DotObservifyPipe, DotOlPipe, DotOperationService, DotParagraphPipe, DotParseIntPipe, DotPathWalkPipe, DotPoolComponent, DotPromisifyPipe, DotRandomPipe, DotRemoveHtmlPipe, DotRenderDatePipe, DotRenderHostPipe, DotRenderTelPipe, DotRendererService, DotResizeEventService, DotRgbPipe, DotRoundPipe, DotScrollEventService, DotSignalService, DotStyleService, DotStyleVarPipe, DotTextComponent, DotToBooleanPipe, DotToFitPipe, DotToFixedPipe, DotToLocaleStringPipe, DotToNumberPipe, DotToPluralPipe, DotToStringPipe, DotToWrapStringPipe, DotTrustHtmlPipe, DotTrustUrlPipe, DotTypeOfPipe, DotUlPipe, DotUrlToAnchorPipe, DotValuesPipe, DotZoomService, DotZoomableDirective, ElementMover, ElementZoomer, InheritBehaviorSubject, InheritSubject, MixinComponent, NgClassSwitch, NgClassToggle, OperationAction, SubscribeCycleBehaviorSubject, SubscribeCycleSubject, WithAsyncBase, destroy, destroyMap, getNode, getTracker, invertAll, invertAllEachFunction, invertsAll, invertsAllEachFunction, lazySet, selectAll, selectAllEachFunction, selectsAll, selectsAllEachFunction, unselectAll, unselectAllEachFunction, unselectsAll, unselectsAllEachFunction, valueEntries };
